import React, { useState } from 'react';

import {
	Alert, Button,
	Table, Form, Input,
	Modal, ModalHeader, ModalBody, ModalFooter
} from 'reactstrap'

import { getFormFields } from '../../api'

const defaultShareTemplate = {
	id: 0,
	type: 'share',
	title  : "",
	subject: "[company_name] | [job_name]",
	body   : "Hi,\n\nPlease fill out the form located at the link below:\n\n[job_name]\n[link_address]\n\nThanks,\n[user_name]\n[user_email]"
}

const defaultReviewTemplate = {
	id: 0,
	type: 'review',
	title  : "",
	subject: "Job Information for [job_name] is ready for review",
	body   : "Job Information is complete for [job_name].\n\nDetails:\nJob Name: [job_name]\nLocation: [location]\nContact: [contact]\nReference #: [reference_no]\nCompany Name: [customer_company]\nCustomer Name: [customer_name]\n\n[login_link] to review the information and submit service request to NCS.\n\nQuestions? Email SecureYourTomorrow@ncscredit.com or call 800-826-5256."
}

const shareMergeFields = [ 
	{ value: '[link_address]', title: 'dynamically-created link to this job' },
	{ value: '[job_number]', title: 'Job Number' },  // Customer Reference Number
	{ value: '[customer_name]', title: 'Customer Name' },
	{ value: '[job_name]', title: 'Job Name' },
	{ value: '[company_name]', title: 'Company Name' },
	{ value: '[user_name]', title: 'User Name (Your Name)' },
	{ value: '[user_email]', title: 'User Email (Your Email)' }
];

const reviewMergeFields = [
	{ value: '[job_name]', title: 'Job Name' },
	{ value: '[company_name]', title: 'Company Name' },
	{ value: '[reference_no]', title: 'Reference Number' },
	{ value: '[location]', title: 'Location' },
	{ value: '[contact]', title: 'Contact' },
	{ value: '[customer_company]', title: 'Customer Company Name' },
	{ value: '[customer_name]', title: 'Customer Contact Name' },
	{ value: '[login_link]', title: 'Login Link' },
	{ value: '[user_name]', title: 'Recipient Name' },
	{ value: '[user_email]', title: 'Recipient Email' }
];

const EmailTemplates = ({ templates, saveEmailTemplate, removeEmailTemplate }) => {

	/* ************************************************************************ */
	/* ***** Email Template Functions *********************************** ***** */
	/* ************************************************************************ */
	const [ emailTemplateModal, setEmailTemplateModal ] = useState(false);
	const [ editTemplate, setEditTemplate ] = useState(defaultShareTemplate);

	const [ copiedField, setCopiedField ] = useState(null);
	const [ isSaving, setIsSaving ] = useState(false);

	// Browse Fields modal state
	const [ browseFieldsModal, setBrowseFieldsModal ] = useState(false);
	const [ formFields, setFormFields ] = useState([]);
	const [ loadingFields, setLoadingFields ] = useState(false);

	// Check if a review template already exists
	const hasReviewTemplate = templates.some(t => t.type === 'review');

	const toggleEmailTemplateModal = () => {
		setEmailTemplateModal(!emailTemplateModal);
	}

	const addNewEmailTemplate = (type = 'share') => {
		if (type === 'review') {
			setEditTemplate(defaultReviewTemplate);
		} else {
			setEditTemplate(defaultShareTemplate);
		}
		setEmailTemplateModal(true);
	}

	const editEmailTemplate = (template) => {
		console.log('editEmailTemplate', template);

		setEmailTemplateModal(true);
		setEditTemplate(template);
	}

	const saveEmailTemplateData = () => {
		setIsSaving(true);
		saveEmailTemplate(editTemplate);
		setEmailTemplateModal(false);
		setIsSaving(false);
	}

	const handleChange = e => {
		const { name, value } = e.target;
		setEditTemplate({
			...editTemplate,
			[name]: value
		});
	}

	const handleTypeChange = e => {
		const newType = e.target.value;
		// When changing type, reset to appropriate default template (keeping id and title)
		if (newType === 'review') {
			setEditTemplate({
				...defaultReviewTemplate,
				id: editTemplate.id,
				title: editTemplate.title
			});
		} else {
			setEditTemplate({
				...defaultShareTemplate,
				id: editTemplate.id,
				title: editTemplate.title
			});
		}
	}

	const copyToClipboard = (text) => {
		navigator.clipboard.writeText(text);
		setCopiedField(text);
		setTimeout(() => {
			setCopiedField(null);
		}, 2000);
	}

	/* ************************************************************************ */
	/* ***** Browse Fields Modal Functions ****************************** ***** */
	/* ************************************************************************ */
	const openBrowseFields = () => {
		setLoadingFields(true);
		setBrowseFieldsModal(true);
		
		getFormFields().then(data => {
			setFormFields(data);
			setLoadingFields(false);
		}).catch(err => {
			console.error('Error loading form fields:', err);
			setLoadingFields(false);
		});
	}

	const closeBrowseFields = () => {
		setBrowseFieldsModal(false);
	}

	const copyFieldMergeTag = (fieldId) => {
		const mergeTag = `[field:${fieldId}]`;
		copyToClipboard(mergeTag);
	}

	// Get the appropriate merge fields based on template type
	const mergeFields = editTemplate.type === 'review' ? reviewMergeFields : shareMergeFields;

	const { id, type, title, subject, body } = editTemplate;

	// Check if type can be changed (can't change to review if one already exists, unless editing the review template)
	const canSelectReview = !hasReviewTemplate || (id && templates.find(t => t.id === id)?.type === 'review');

	return (
		<>
			{/* Browse Fields Modal */}
			<Modal isOpen={browseFieldsModal} toggle={closeBrowseFields} style={{ 'maxWidth': '800px' }}>
				<ModalHeader toggle={closeBrowseFields}>
					Browse Form Fields
				</ModalHeader>
				<ModalBody>
					<p className="text-muted mb-3">
						Click on a field to copy its merge tag. Use <code>[field:ID]</code> syntax in your template.
						<br />
						<small>Note: If a field doesn't exist on a job's form, it will be replaced with an empty value.</small>
					</p>
					
					{loadingFields ? (
						<div className="text-center py-4">
							<i className="fas fa-spinner fa-spin mr-2"></i> Loading fields...
						</div>
					) : formFields.length === 0 ? (
						<Alert color="warning">No active forms found for this company.</Alert>
					) : (
						formFields.map((form, formIdx) => (
							<div key={formIdx} className="mb-4">
								<h5 className="border-bottom pb-2">{form.form_name}</h5>
								<Table size="sm" hover>
									<thead>
										<tr>
											<th>Field Title</th>
											<th>Type</th>
											<th>Section</th>
											<th>Merge Tag</th>
										</tr>
									</thead>
									<tbody>
										{form.fields.map((field, fieldIdx) => (
											<tr 
												key={fieldIdx} 
												className="cursor-pointer"
												onClick={() => copyFieldMergeTag(field.id)}
												style={{ cursor: 'pointer' }}
											>
												<td>{field.title}</td>
												<td><span className="badge badge-secondary">{field.type}</span></td>
												<td><small className="text-muted">{field.section}</small></td>
												<td>
													<code>[field:{field.id}]</code>
													{copiedField === `[field:${field.id}]` && (
														<span className="text-success ml-2">
															<i className="fas fa-check"></i> copied
														</span>
													)}
												</td>
											</tr>
										))}
									</tbody>
								</Table>
							</div>
						))
					)}
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" onClick={closeBrowseFields}>Close</Button>
				</ModalFooter>
			</Modal>

			{/* Email Template Edit Modal */}
			<Modal isOpen={emailTemplateModal} toggle={toggleEmailTemplateModal} style={{ 'maxWidth': '1024px' }}>
				<ModalHeader toggle={toggleEmailTemplateModal}>
					<span>{id ? 'Edit Email Template' : 'Add New Email Template'}</span>
				</ModalHeader>
				<ModalBody>
					<div className="form-group">
						<label htmlFor="type">Template Type</label>
						<select 
							name="type" 
							className="form-control" 
							value={type || 'share'} 
							onChange={handleTypeChange}
							disabled={id !== 0} // Can't change type when editing existing template
						>
							<option value="share">Share (for sharing jobs with recipients)</option>
							{canSelectReview && (
								<option value="review">Review Notification (for finalized job alerts)</option>
							)}
						</select>
						{type === 'review' && (
							<small className="text-muted">
								This template will be used for email notifications sent when a job is finalized for review.
							</small>
						)}
						{!canSelectReview && id === 0 && (
							<small className="text-warning">
								A review notification template already exists. Only one is allowed per company.
							</small>
						)}
					</div>
					<div className="form-group">
						<label htmlFor="title">Title</label>
						<Input type="text" name="title" placeholder="Title" value={title} onChange={handleChange} />
					</div>
					<div className="form-group">
						<label htmlFor="subject">Subject</label>
						<Input type="text" name="subject" placeholder="Subject" value={subject} onChange={handleChange} />

					</div>
					<div className="d-flex">
						<div className="form-group w-75">
							<label htmlFor="body">Body</label>
							<textarea name="body" className="form-control" rows="10" value={body} onChange={handleChange}></textarea>
						</div>
						<div className="w-25 p-3">
							<cite style={{ fontSize: '.9rem' }}>
								<h6 title="The following merge fields can be included in the Subject and/or Body">Merge Fields</h6>
								<ul className="list-unstyled">
									{mergeFields.map((mf, idx) => (
										<li key={idx} className="cursor-pointer merge-field-item" onClick={() => copyToClipboard(mf.value)}>
											<span title={mf.title}>{mf.value}</span>{' '}
											{copiedField === mf.value ? (
												<><i className="fas fa-check ml-1"></i> <span className="small">copied</span></>
											) : (
												<i className="far fa-copy ml-1 hover-icon"></i>
											)}
										</li>
									))}
								</ul>
								<hr />
								<h6>Form Fields</h6>
								<p className="small text-muted">
									Use <code>[field:ID]</code> to include form field values.
								</p>
								<Button size="sm" color="info" onClick={openBrowseFields}>
									<i className="fas fa-search mr-1"></i> Browse Fields
								</Button>
							</cite>
						</div>
					</div>
				</ModalBody>
				<ModalFooter className="justify-content-between">
					<Button type="button" color="danger" onClick={toggleEmailTemplateModal}>Cancel</Button>
					<Button color={isSaving || (!title || !subject || !body) ? "secondary" : "primary"} className="ml-3" onClick={saveEmailTemplateData} disabled={isSaving || (!title || !subject || !body)}>Save</Button>
				</ModalFooter>
			</Modal>

			<h4>
				Email Templates
				<Button size="sm" color="primary" className="ml-3" onClick={() => addNewEmailTemplate('share')}>Add Share Template</Button>
				{!hasReviewTemplate && (
					<Button size="sm" color="info" className="ml-2" onClick={() => addNewEmailTemplate('review')}>Add Review Template</Button>
				)}
			</h4>
		
			{templates.length > 0 && (
				<div className="input-group">
					<Table responsive className="mt-3">
						<thead>
							<tr>
								<th>&nbsp;</th>
								<th>Type</th>
								<th>Title</th>
								<th>Subject</th>
								<th>Body</th>
								<th>&nbsp;</th>
							</tr>
						</thead>
						<tbody>
							{templates.map((template, index) => {
								return (<tr key={index}>
									<td>{index + 1}</td>
									<td>
										<span className={`badge badge-${template.type === 'review' ? 'info' : 'secondary'}`}>
											{template.type === 'review' ? 'Review' : 'Share'}
										</span>
									</td>
									<td>{template.title}</td>
									<td>{template.subject}</td>
									<td>{template.body}</td>
									<td className="d-flex">
										<Button size="sm" color="secondary" data-template-id={template.id} onClick={() => editEmailTemplate(template)}>Edit</Button>
										<Button size="sm" color="danger" data-template-id={template.id} className="ml-3" onClick={() => removeEmailTemplate(template)}>Delete</Button>
									</td>
								</tr>)
							})}
						</tbody>
					</Table>
				</div>
			)}
		</>
	)
}

export default EmailTemplates;
